import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from './api';
import './Admin.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    contacted: 0,
    completed: 0,
    cancelled: 0,
    recentRegistrations: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getStats();
      
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { class: 'bg-warning', text: 'Mới' },
      pending: { class: 'bg-warning', text: 'Chờ xử lý' },
      contacted: { class: 'bg-info', text: 'Đã liên hệ' },
      in_progress: { class: 'bg-primary', text: 'Đang xử lý' },
      completed: { class: 'bg-success', text: 'Hoàn thành' },
      cancelled: { class: 'bg-danger', text: 'Đã hủy' },
    };
    const badge = badges[status] || { class: 'bg-secondary', text: status };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Đang tải dữ liệu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <i className="fas fa-exclamation-triangle"></i>
        <h3>Có lỗi xảy ra</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchStats}>
          <i className="fas fa-redo me-2"></i>
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>
          <i className="fas fa-tachometer-alt me-3"></i>
          Dashboard
        </h1>
        <p>Tổng quan về hệ thống đăng ký tư vấn</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Tổng đăng ký</p>
          </div>
        </div>

        <div className="stat-card pending">
          <div className="stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.new || 0}</h3>
            <p>Mới</p>
          </div>
        </div>

        <div className="stat-card contacted">
          <div className="stat-icon">
            <i className="fas fa-phone-alt"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.contacted || 0}</h3>
            <p>Đã liên hệ</p>
          </div>
        </div>

        <div className="stat-card completed">
          <div className="stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.completed || 0}</h3>
            <p>Hoàn thành</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>
          <i className="fas fa-bolt me-2"></i>
          Thao tác nhanh
        </h2>
        <div className="actions-grid">
          <Link to="/admin/registrations" className="action-card">
            <i className="fas fa-list"></i>
            <span>Xem tất cả đăng ký</span>
          </Link>
          <Link to="/admin/registrations?status=new" className="action-card highlight">
              <i className="fas fa-hourglass-half"></i>
              <span>Xử lý đăng ký mới ({stats.new || 0})</span>
            </Link>
          <button className="action-card" onClick={fetchStats}>
            <i className="fas fa-sync-alt"></i>
            <span>Làm mới dữ liệu</span>
          </button>
        </div>
      </div>

      {/* Recent Registrations */}
      <div className="recent-registrations">
        <div className="section-header">
          <h2>
            <i className="fas fa-history me-2"></i>
            Đăng ký gần đây
          </h2>
          <Link to="/admin/registrations" className="view-all-link">
            Xem tất cả <i className="fas fa-arrow-right ms-1"></i>
          </Link>
        </div>

        {stats.recentRegistrations.length === 0 ? (
          <div className="no-data">
            <i className="fas fa-inbox"></i>
            <p>Chưa có đăng ký nào</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Họ tên</th>
                  <th>Số điện thoại</th>
                  <th>Tỉnh/Thành phố</th>
                  <th>Trạng thái</th>
                  <th>Ngày đăng ký</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentRegistrations.map((reg, index) => (
                  <tr key={reg.id}>
                    <td>{index + 1}</td>
                    <td>
                      <strong>{reg.full_name}</strong>
                    </td>
                    <td>
                      <a href={`tel:${reg.phone}`} className="phone-link">
                        <i className="fas fa-phone me-1"></i>
                        {reg.phone}
                      </a>
                    </td>
                    <td>{reg.province}</td>
                    <td>{getStatusBadge(reg.status)}</td>
                    <td>{formatDate(reg.created_at)}</td>
                    <td>
                      <Link 
                        to={`/admin/registrations/${reg.id}`} 
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;